/**
 * 🏗️ ARQUITETURA DE 100K USUÁRIOS - Fase 1: Student Auth Store
 * 
 * Store Zustand para autenticação de estudantes, separado do auth principal.
 * ✅ AUDIT FIX: Usa storage wrapper seguro ao invés de localStorage direto
 * ✅ Sprint 3: Use centralized time constants
 * ✅ Admin Impersonation: Permite admins visualizarem como estudante
 * ✅ AUDIT FIX: Migrado console.* para logger.*
 */

import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { storage } from '@/lib/storage';
import { SESSION_DURATION } from '@/constants/time';
import { useAuthStore } from './authStore';
import { logger } from '@/lib/logger';

interface StudentSession {
  studentId: string;
  studentName: string;
  studentAge: number;
  familyId: string;
  expiresAt: number;
  avatarPreference?: string | null;
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface StudentAuthState {
  studentSession: StudentSession | null;
  isStudentLoggedIn: boolean;
  isLoading: boolean;
  isImpersonating: boolean;

  loginStudent: (identifier: string, credential: string, useUsername?: boolean) => Promise<LoginResult>;
  logoutStudent: () => Promise<void>;
  verifySession: () => Promise<boolean>;
  initialize: () => void;
  impersonateStudent: (studentId: string) => Promise<boolean>;
  stopImpersonating: () => void;
  updateSessionData: (updates: Partial<Pick<StudentSession, 'studentName' | 'avatarPreference'>>) => void;
}

export const useStudentAuthStore = create<StudentAuthState>((set, get) => ({
  studentSession: null,
  isStudentLoggedIn: false,
  isLoading: true,
  isImpersonating: false,

  initialize: () => {
    const storedSession = storage.get<StudentSession | null>('student_session', null);
    const isImpersonating = storage.get<boolean>('student_impersonating', false);
    
    if (storedSession) {
      const now = Date.now();
      
      if (storedSession.expiresAt > now) {
        set({ 
          studentSession: storedSession, 
          isStudentLoggedIn: true,
          isLoading: false,
          isImpersonating,
        });
        useAuthStore.getState().setActiveMode('student');
        logger.debug('[studentAuthStore] Session restored from storage', isImpersonating ? '(impersonating)' : '');
      } else {
        storage.remove('student_session');
        storage.remove('student_impersonating');
        logger.debug('[studentAuthStore] Session expired, cleared');
        set({ isLoading: false });
      }
    } else {
      set({ isLoading: false });
    }
  },

  impersonateStudent: async (studentId: string) => {
    try {
      logger.debug('[studentAuthStore] Admin impersonating student:', studentId);

      const { data, error } = await supabase.functions.invoke('admin-impersonate-student', {
        body: { student_id: studentId, action: 'start' },
      });

      if (error || !data?.success) {
        logger.error('[studentAuthStore] Impersonation failed:', error?.message || data?.error);
        return false;
      }

      const session: StudentSession = {
        studentId: data.student_id,
        studentName: data.student_name,
        studentAge: data.student_age || 0,
        familyId: data.family_id,
        avatarPreference: data.avatar_preference,
        expiresAt: Date.now() + SESSION_DURATION.STUDENT,
      };

      storage.set('student_session', session);
      storage.set('student_impersonating', true);
      set({ studentSession: session, isStudentLoggedIn: true, isImpersonating: true });
      useAuthStore.getState().setActiveMode('student');
      
      logger.debug('[studentAuthStore] Impersonation started for:', data.student_name);
      return true;
    } catch (error) {
      logger.error('[studentAuthStore] Impersonation error:', error);
      return false;
    }
  },

  stopImpersonating: () => {
    const session = get().studentSession;
    
    storage.remove('student_session');
    storage.remove('student_impersonating');
    
    set({ studentSession: null, isStudentLoggedIn: false, isImpersonating: false });
    
    const authStore = useAuthStore.getState();
    if (authStore.availableModes.includes('admin')) {
      authStore.setActiveMode('admin');
      logger.debug('[studentAuthStore] Restored activeMode to admin');
    }
    
    logger.debug('[studentAuthStore] Impersonation ended - state cleared synchronously');
    
    if (session) {
      setTimeout(() => {
        supabase.functions.invoke('admin-impersonate-student', {
          body: { student_id: session.studentId, action: 'end' },
        }).catch(err => logger.warn('[studentAuthStore] Failed to log impersonation end:', err));
      }, 0);
    }
  },

  loginStudent: async (identifier: string, credential: string, useUsername = false) => {
    try {
      logger.debug('[studentAuthStore] Attempting student login...');

      const { data: edgeFunctionData, error: edgeFunctionError } = await supabase.functions.invoke(
        'student-auth',
        {
          body: { identifier, credential, useUsername },
        }
      );

      if (edgeFunctionError) {
        logger.warn('[studentAuthStore] Edge function failed, trying RPC fallback:', edgeFunctionError.message);
        
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          'verify_student_credentials_v2',
          {
            p_identifier: identifier,
            p_credential: credential,
            p_use_username: useUsername,
          }
        );

        if (rpcError || !rpcData) {
          logger.error('[studentAuthStore] RPC fallback also failed:', rpcError);
          return { success: false, error: 'Erro de conexão. Tente novamente.' };
        }

        const rpcResult = rpcData as { 
          success?: boolean; 
          error?: string;
          student_id?: string; 
          student_name?: string; 
          student_age?: number; 
          family_id?: string; 
          avatar_preference?: string | null 
        };

        if (rpcResult.success === false) {
          logger.error('[studentAuthStore] RPC returned error:', rpcResult.error);
          return { success: false, error: rpcResult.error || 'Credenciais inválidas' };
        }

        if (!rpcResult.student_id || !rpcResult.student_name || !rpcResult.family_id) {
          logger.error('[studentAuthStore] Invalid RPC response - missing required fields');
          return { success: false, error: 'Erro interno. Tente novamente.' };
        }

        const session: StudentSession = {
          studentId: rpcResult.student_id,
          studentName: rpcResult.student_name,
          studentAge: rpcResult.student_age || 0,
          familyId: rpcResult.family_id,
          avatarPreference: rpcResult.avatar_preference,
          expiresAt: Date.now() + SESSION_DURATION.STUDENT,
        };

        storage.set('student_session', session);
        set({ studentSession: session, isStudentLoggedIn: true, isImpersonating: false });
        useAuthStore.getState().setActiveMode('student');
        logger.debug('[studentAuthStore] Login successful via RPC');
        return { success: true };
      }

      if (!edgeFunctionData?.success) {
        logger.error('[studentAuthStore] Edge function returned error:', edgeFunctionData?.error);
        return { success: false, error: edgeFunctionData?.error || 'Credenciais inválidas' };
      }

      if (!edgeFunctionData.student_id || !edgeFunctionData.student_name || !edgeFunctionData.family_id) {
        logger.error('[studentAuthStore] Invalid edge function response - missing required fields');
        return { success: false, error: 'Erro interno. Tente novamente.' };
      }

      const session: StudentSession = {
        studentId: edgeFunctionData.student_id,
        studentName: edgeFunctionData.student_name,
        studentAge: edgeFunctionData.student_age || 0,
        familyId: edgeFunctionData.family_id,
        avatarPreference: edgeFunctionData.avatar_preference,
        expiresAt: Date.now() + SESSION_DURATION.STUDENT,
      };

      storage.set('student_session', session);
      set({ studentSession: session, isStudentLoggedIn: true, isImpersonating: false });
      useAuthStore.getState().setActiveMode('student');
      logger.debug('[studentAuthStore] Login successful via Edge Function');
      return { success: true };

    } catch (error) {
      logger.error('[studentAuthStore] Login error:', error);
      return { success: false, error: 'Erro inesperado. Tente novamente.' };
    }
  },

  logoutStudent: async () => {
    try {
      storage.remove('student_session');
      storage.remove('student_impersonating');
      set({ studentSession: null, isStudentLoggedIn: false, isImpersonating: false });
      useAuthStore.getState().setActiveMode(null);
      logger.debug('[studentAuthStore] Student logged out');
    } catch (error) {
      logger.error('[studentAuthStore] Logout error:', error);
      storage.remove('student_session');
      storage.remove('student_impersonating');
      set({ studentSession: null, isStudentLoggedIn: false, isImpersonating: false });
    }
  },

  verifySession: async () => {
    const session = get().studentSession;
    if (!session) return false;

    const now = Date.now();
    if (session.expiresAt <= now) {
      logger.debug('[studentAuthStore] Session expired');
      await get().logoutStudent();
      return false;
    }

    return true;
  },

  updateSessionData: (updates) => {
    const session = get().studentSession;
    if (!session) return;
    const updated = { ...session, ...updates };
    storage.set('student_session', updated);
    set({ studentSession: updated });
    logger.debug('[studentAuthStore] Session data updated:', Object.keys(updates).join(', '));
  },
}));
